import * as _ from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { connect, Dispatch } from "react-redux";
import { IApplicationState, AppActionCreators, IMicrositeListResponse, MicrositeActionCreators, IMicrositeLookupDataResponse } from "@nkr/store/index";
import { IBaseResponse, IListItem, IMicrositeRecipient, IMicrositeRecipientForList } from '@nkr/models/index';
import { Col, FormControl, Row } from 'react-bootstrap';
import { AgGridReact } from "ag-grid-react";
import { ColDef, IHeaderParams, ICellRendererParams, FirstDataRenderedEvent, GridReadyEvent } from "ag-grid-community";
import { MicrositeEmailType } from '@nkr/enums/microsite-email-type';
import { toast } from 'react-toastify';
import { MicrositesGridWrapper } from './styles';
import SortableHeaderComponent from "@nkr/views/admin/center/list/common/sortable-header";
import OverlayMenuButton from '@nkr/components/overlay-menu-button';

interface IEmailGroup {
    name: string,
    emails: IListItem[]
}

interface IProps { }

interface IStateProps {
}

interface IDispatchProps {
    showLoading: (message?: string) => Promise<void>,
    hideLoading: () => void,
    getMicrositeList: (centerId: number) => Promise<IMicrositeListResponse>,
    getLookupData: (centerId: number, micrositeRecipientId: number) => Promise<IMicrositeLookupDataResponse>,
    sendEmail: (id: number, emailType: MicrositeEmailType) => Promise<IBaseResponse>
}

type TProps = IProps & IStateProps & IDispatchProps;

const MicrositeList = (props: TProps) => {
    const [centerId, setCenterId] = useState<number>(0);
    const [list, setList] = useState<IMicrositeRecipientForList[]>([]);
    const [emailTypeList, setEmailTypeList] = useState<IListItem[]>([]);

    const getData = () => {
        props.showLoading('Getting microsites, one moment please...').then(() => {
            Promise.all([
                props.getMicrositeList(null),
                props.getLookupData(0, 0)
            ])
                .then(([response, emailTypeResponse]) => {
                    if (response.data.success && emailTypeResponse.data.success) {
                        setList(response.data.results.list);
                        setEmailTypeList(emailTypeResponse.data.results.emailTypeList);
                    }

                    props.hideLoading();
                });
        });
    };

    const getEmailList = () => {
        let list = _.sortBy(emailTypeList.filter(x => x.sortOrder >= 0), [item => item.description], [item => item.value]);
        let groups = _.uniq(list.map(x => x.value));

        let results: IEmailGroup[] = groups.map(group => {
            return {
                name: group,
                emails: list.filter(x => x.value == group)
            };
        });

        return results;
    };

    const handleCenterChange = (e: React.FormEvent<FormControl & HTMLSelectElement>) => {
        setCenterId(parseInt(e.currentTarget.value));
    };

    const handleActionSelect = (id: number, emailType: MicrositeEmailType) => {
        props.showLoading('Sending email, one moment please...').then(() => {
            props.sendEmail(id, emailType).then(response => {
                if (response.data.success) {
                    toast.success('Email sent successfully');
                }

                props.hideLoading();
            });
        });
    };

    useEffect(() => {
        getData();
    }, []);

    const emailGroups = getEmailList();

    const columns: ColDef[] = [
        {
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>ID</SortableHeaderComponent>,
            headerName: 'ID',
            field: 'id',
            width: 40,
            cellClass: 'list-cell',
            headerClass: 'list-header',
            sortable: true
        },
        {
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>First Name</SortableHeaderComponent>,
            headerName: 'First Name',
            field: 'firstName',
            width: 60,
            cellClass: 'list-cell',
            headerClass: 'list-header',
            sortable: true
        },
        {
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Last Name</SortableHeaderComponent>,
            headerName: 'Last Name',
            field: 'lastName',
            width: 60,
            cellClass: 'list-cell',
            headerClass: 'list-header',
            sortable: true
        },
        {
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Email</SortableHeaderComponent>,
            headerName: 'Email',
            field: 'email',
            width: 200,
            cellClass: 'list-cell',
            headerClass: 'list-header',
            sortable: true
        },
        {
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Center</SortableHeaderComponent>,
            headerName: 'Center',
            field: 'center.alias',
            width: 75,
            cellClass: 'list-cell',
            headerClass: 'list-header',
            sortable: true,
            cellRendererFramework: (params:ICellRendererParams)=> {
                let data = params.data as IMicrositeRecipientForList;

                if (data.center == null) {
                    return <>- NO CENTER -</>
                }

                return <>{params.value}</>
            }
        },
        {
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Coach</SortableHeaderComponent>,
            headerName: 'Coach',
            field: 'coachContactName',
            width: 90,
            cellClass: 'list-cell',
            headerClass: 'list-header',
            sortable: true
        },
        {
            headerName: '',
            field: 'id',
            width: 40,
            cellClass: 'action-menu',
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IMicrositeRecipient;

                // return (
                //     <DropdownWrapper className='qa-microsite-actions'>
                //         <DropdownButton bsStyle='primary' bsSize='xs' pullRight title='Actions' id={'actions-' + data.id}>
                //             {emailTypeList.map(item => {
                //                 return (
                //                     <MenuItem eventKey={item.id} onSelect={(e: any) => handleActionSelect(data.id, e)}>{item.description}</MenuItem>
                //                 );
                //             })}
                //         </DropdownButton>
                //     </DropdownWrapper>
                // );

                let menu = emailTypeList.map(x => {
                    return { id: data.id, action: x.id, include: true, selectable: true, element: <>{x.description}</> };
                });

                return (
                    <OverlayMenuButton
                        menu={menu}
                        bsStyle='primary'
                        bsSize='xs'
                        title='Send Email'
                        onSelect={handleActionSelect} />
                )
            }
        }
    ];

    const columnDefs = useMemo(() => columns, [emailTypeList]);

    const centerList = _.uniqBy(list.filter(x => x.center != null).map(x => {
        return {
            id: x.center.id,
            alias: x.center.alias
        };
    }), item => item.id);

    const sortedCenterList = _.sortBy(centerList, x => x.alias);

    const filteredList = list.filter(x => {
        if (centerId > 0) {
            return x.center != null && x.center.id == centerId;
        } else if (centerId == -1) {
            return x.center == null;
        }

        return true;
    });

    const rowData = useMemo(() => filteredList, [filteredList]);

    if (emailTypeList.length == 0)
        return null;

    return (
        <>
            <div className="container">
                <Row>
                    <Col xs={12} lg={12}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", height: 30, marginBottom: 20 }}>
                            <div style={{ fontSize: 28, fontWeight: 500 }}>
                                QA - Microsites
                            </div>
                            <div>
                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                    <div style={{ marginRight: 5 }}>Center:</div>
                                    <FormControl
                                        componentClass='select'
                                        value={centerId}
                                        onChange={handleCenterChange}
                                    >
                                        <option value={0}>- All Centers -</option>
                                        <option value={-1}>- No Center -</option>
                                        {sortedCenterList.map(item =>
                                            <option value={item.id}>{item.alias}</option>
                                        )}
                                    </FormControl>
                                </div>
                            </div>
                        </div>
                    </Col>
                </Row>

                <MicrositesGridWrapper className="ag-theme-bootstrap">
                    <AgGridReact
                        columnDefs={columnDefs}
                        rowData={rowData}
                        // headerHeight={30}
                        rowHeight={35}
                        suppressCellSelection={true}
                        suppressRowClickSelection={true}
                        onGridReady={(e: GridReadyEvent) => {
                            e.api.sizeColumnsToFit();
                        }}
                        onFirstDataRendered={(e: FirstDataRenderedEvent) => {
                            e.api.sizeColumnsToFit();
                        }}
                    >
                    </AgGridReact>
                </MicrositesGridWrapper>
            </div>
        </>
    );
}

const mapStateToProps = (appState: IApplicationState): IStateProps => {
    return {
    }
}

const mapDispatchToProps = (dispatch: Dispatch<IApplicationState>): IDispatchProps => {
    return {
        showLoading: (message) => {
            return dispatch(AppActionCreators.showLoadingAsync(message));
        },
        hideLoading: () => {
            dispatch(AppActionCreators.hideLoading());
        },
        getMicrositeList: (centerId) => {
            return dispatch(MicrositeActionCreators.getMicrositeList(null, centerId, null, null, '', '', '', '', null, 0));
        },
        getLookupData: (centerId, micrositeRecipientId) => {
            return dispatch(MicrositeActionCreators.getLookupData(centerId, micrositeRecipientId));
        },
        sendEmail: (id, emailType) => {
            return dispatch(MicrositeActionCreators.sendEmail(id, emailType));
        },
    };
}

export default connect<IStateProps, IDispatchProps, IProps>(
    mapStateToProps,
    mapDispatchToProps)(MicrositeList);